import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  addWorkspaceCustomDomain,
  checkWorkspaceCustomDomain,
  fetchWorkspaceCustomDomains,
  makeWorkspaceCustomDomainPrimary,
  removeWorkspaceCustomDomain,
} from './customDomainsApi';
import type { CustomDomain } from './customDomainsApi.types';
import { resolveCustomDomainRefetchInterval } from './customDomainPolling';
import { WORKSPACE_CUSTOM_DOMAINS_QUERY_KEY } from './customDomainsQueryKeys';

export function useWorkspaceCustomDomainsQuery(
  isEnabled: boolean
): UseQueryResult<CustomDomain[], Error> {
  return useQuery({
    queryKey: WORKSPACE_CUSTOM_DOMAINS_QUERY_KEY,
    queryFn: fetchWorkspaceCustomDomains,
    enabled: isEnabled,
    refetchInterval: function resolveInterval(query) {
      return resolveCustomDomainRefetchInterval(query.state.data);
    },
  });
}

function useInvalidateWorkspaceCustomDomains(): () => Promise<void> {
  const queryClient = useQueryClient();
  return function invalidateWorkspaceCustomDomains() {
    return queryClient.invalidateQueries({ queryKey: WORKSPACE_CUSTOM_DOMAINS_QUERY_KEY });
  };
}

export function useAddCustomDomainMutation(): UseMutationResult<CustomDomain, Error, string> {
  const invalidateWorkspaceCustomDomains = useInvalidateWorkspaceCustomDomains();
  return useMutation({
    mutationFn: addWorkspaceCustomDomain,
    onSuccess: invalidateWorkspaceCustomDomains,
  });
}

export function useCheckCustomDomainMutation(): UseMutationResult<CustomDomain, Error, string> {
  const invalidateWorkspaceCustomDomains = useInvalidateWorkspaceCustomDomains();
  return useMutation({
    mutationFn: checkWorkspaceCustomDomain,
    onSuccess: invalidateWorkspaceCustomDomains,
  });
}

export function useMakeCustomDomainPrimaryMutation(): UseMutationResult<
  CustomDomain,
  Error,
  string
> {
  const invalidateWorkspaceCustomDomains = useInvalidateWorkspaceCustomDomains();
  return useMutation({
    mutationFn: makeWorkspaceCustomDomainPrimary,
    onSuccess: invalidateWorkspaceCustomDomains,
  });
}

export function useRemoveCustomDomainMutation(): UseMutationResult<void, Error, string> {
  const invalidateWorkspaceCustomDomains = useInvalidateWorkspaceCustomDomains();
  return useMutation({
    mutationFn: removeWorkspaceCustomDomain,
    onSuccess: invalidateWorkspaceCustomDomains,
  });
}
